import React from 'react';
import { Link } from 'react-router-dom';
import {
  Link as MuiLink,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  TableRow,
  TableCell,
  Typography,
} from '@mui/material';
import {
  MoreHoriz,
  Print,
  MailOutline,
  Edit,
  Refresh,
} from '@mui/icons-material';

import InvoiceItemFormattedMessages from '../../../messages/billing/InvoiceItemFormattedMessages';
import { formatCurrency } from '../../../utils/currency';

let invShowUrl = '/billing/invoiceshow/';

const InvoiceTableRow = ({
  id,
  provider,
  invoiceId,
  billId,
  customerId,
  name,
  invoiceDate,
  paidDate,
  status,
  currency,
  dollar,
  customerType,
  amount,
  isGodAdmin,
  emailRequestHandler,
  printRequestHandler,
  adjustRequestHandler,
  refreshRequestHandler,
  errorHandler,
  processPendingInvoice,
  transitionPendingToInvoiced,
  transitionBillToFailed,
  cancelPendingTransaction,
  dataChange,
  selectCustomer,
}) => {
  const displayAmount = formatCurrency(amount, currency);

  let customerTypeText;
  if (isGodAdmin && customerType) {
    if (customerType === 'A') customerTypeText = 'Post';
    else if (customerType === 'B') customerTypeText = 'Pre';
    else customerTypeText = 'Other';
  }

  // Recalculate is disabled for
  let disableRecalculate =
    (status !== 'Pending' && status !== 'Invoiced') || customerType === 'B';

  return (
    <TableRow id={id}>
      {_generateProviderName(isGodAdmin, provider)}
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <MuiLink component={Link} to={`${invShowUrl}${customerId}/${billId}`}>
          {invoiceId}
        </MuiLink>
      </TableCell>
      {_generateCustomerName(isGodAdmin, id, customerId, name, selectCustomer)}
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{invoiceDate}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{paidDate}</TableCell>
      <TableCell>
        {_generateStatus(
          isGodAdmin,
          status,
          customerType,
          invoiceId,
          billId,
          customerId,
          id,
          errorHandler,
          dataChange,
          processPendingInvoice,
          transitionPendingToInvoiced,
          transitionBillToFailed,
          cancelPendingTransaction
        )}
      </TableCell>
      <TableCell sx={{ color: amount < 0 ? 'error.main' : 'inherit' }}>
        {displayAmount}
      </TableCell>
      <TableCell>{dollar}</TableCell>
      {isGodAdmin && <TableCell>{customerTypeText}</TableCell>}
      {_generateActionIcons(
        isGodAdmin,
        id,
        customerId,
        billId,
        disableRecalculate,
        printRequestHandler,
        emailRequestHandler,
        adjustRequestHandler,
        refreshRequestHandler,
        amount < 0
      )}
    </TableRow>
  );
};

const _generateStatus = (
  isGodAdmin,
  status,
  customerType,
  invoiceId,
  billId,
  customerId,
  providerId,
  errorHandler,
  dataChange,
  processPendingInvoice,
  transitionPendingToInvoiced,
  transitionBillToFailed,
  cancelPendingTransaction
) => {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (!status) return;
  else if (status === 'Pending' && !isGodAdmin) {
    return (
      <>
        <MuiLink tabIndex={0} onClick={handleClick}>
          {status}
        </MuiLink>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem onClick={() => processPendingInvoice(billId, customerId)}>
            {InvoiceItemFormattedMessages.processTransaction}
          </MenuItem>
          {customerType === 'B' && (
            <MenuItem
              onClick={() =>
                cancelPendingTransaction(customerId, providerId, billId)
              }
            >
              {InvoiceItemFormattedMessages.cancelTransaction}
            </MenuItem>
          )}
        </Menu>
      </>
    );
  } else {
    return <>{status}</>;
  }
};

const _generateActionIcons = (
  isGodAdmin,
  id,
  customerId,
  billId,
  disableRecalculate,
  printRequestHandler,
  emailRequestHandler,
  adjustRequestHandler,
  refreshRequestHandler,
  disableEdit
) => {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <TableCell>
      <Tooltip title="More actions">
        <IconButton onClick={handleClick}>
          <MoreHoriz />
        </IconButton>
      </Tooltip>
      <Menu
        id="long-menu"
        MenuListProps={{
          'aria-labelledby': 'long-button',
        }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem
          // href={`${invShowUrl}${customerId}/${billId}?print=true`}
          onClick={() => {
            printRequestHandler(`${customerId}/${billId}`);
            handleClose();
          }}
        >
          <Print />
        </MenuItem>
        <MenuItem
          onClick={() => {
            emailRequestHandler();
            handleClose();
          }}
        >
          <MailOutline />
        </MenuItem>
        {isGodAdmin && (
          <>
            <MenuItem
              disabled={disableEdit}
              onClick={() => {
                if (!disableEdit) adjustRequestHandler();
                handleClose();
              }}
            >
              <Edit />
            </MenuItem>
            <MenuItem
              disabled={disableRecalculate}
              onClick={() => {
                if (!disableRecalculate) refreshRequestHandler();
                handleClose();
              }}
            >
              <Refresh />
            </MenuItem>
          </>
        )}
      </Menu>
    </TableCell>
  );
};

const _generateProviderName = (isGodAdmin, provider) => {
  if (!isGodAdmin) return null;
  return <TableCell title={provider}>{provider}</TableCell>;
};

const _generateCustomerName = (
  isGodAdmin,
  id,
  customerId,
  name,
  selectCustomer
) => {
  // Regular User
  if (!isGodAdmin) return null;

  // God Admin
  return (
    <TableCell title={name}>
      <MuiLink tabIndex={0} onClick={() => selectCustomer(customerId)}>
        {name}
      </MuiLink>
    </TableCell>
  );
};

export default InvoiceTableRow;
